import React, { ChangeEventHandler, ComponentType, DetailedHTMLProps, FocusEventHandler, KeyboardEventHandler, MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import { SelectItemRenderer, SelectKeyDown, SelectProps, SelectRenderer, SelectState } from 'react-dropdown-select';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { Select } from 'components/Select';
import theme from 'design/theme';
import { toRem } from 'design/utils';
import { CreatableSelectOption } from './CreatableSelectOption';

export enum CreatableEnterAction {
  Create,
  Select,
  None,
}

export interface ICreatableSelectOption<T> {
  label: string;
  value: T;
  disabled?: boolean;
}

interface IEnterPressResponse<T> {
  action: CreatableEnterAction;
  optionToSelect?: ICreatableSelectOption<T>;
}

interface IProps<T> extends SelectProps<ICreatableSelectOption<T>> {
  className?: string;
  dataCy?: string;
  disableCreationOnEnter?: boolean;
  inputProps?: Omit<
    DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    keyof SelectProps<ICreatableSelectOption<T>> | 'ref' | 'className' | 'disabled' | 'placeholder' | 'readOnly' | 'size' | 'value' | 'onBlur' | 'onChange' | 'onClick' | 'onFocus' | 'onKeyPress'
  >;
  onCreate(item: any): void;
  onEnterPress?(args: SelectRenderer<ICreatableSelectOption<T>> & { inputRef: React.RefObject<HTMLInputElement> }): IEnterPressResponse<T>;
  onInputBlur?: FocusEventHandler<HTMLInputElement>;
  onInputChange?: ChangeEventHandler<HTMLInputElement>;
  onInputClick?: MouseEventHandler;
  onInputFocus?: FocusEventHandler<HTMLInputElement>;
  onInputKeypress?: KeyboardEventHandler<HTMLInputElement>;
  selectId?: string;
}

const CreatableSelectLayout = styled(Select)`
  .react-dropdown-select {
    &-content {
      background: #ffffff;
      border-radius: ${theme.borderRadius.xsmall}px;

      input {
        max-width: 85%;
      }

      span {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        max-width: calc(100% - ${toRem(46)});
        word-wrap: break-word;
        overflow: hidden;
        line-height: ${toRem(16)};
        height: auto;
        max-height: 100%;
      }
    }

    &-dropdown {
      padding: ${toRem(5.5)} 0;
    }

    &-input {
      width: 100%;
      height: 100%;
      margin: 0;
      padding: 0;
      border: none;
      outline: none;
      word-wrap: break-word;
    }

    &-option {
      padding: 0 ${toRem(13.75)};
    }

    &-dropdown-add-new {
      position: relative;
      min-width: 100%;
      max-width: 100%;
      min-height: ${toRem(32)};
      padding: 0 ${toRem(29.75)} 0 ${toRem(13.75)};
      background-color: ${theme.color.white};
      color: ${theme.color.primary[600]};
      word-wrap: break-word;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      border-top: 8px solid transparent;
      border-bottom: 8px solid transparent;
      line-height: ${toRem(16)};

      &:before {
        content: '+';
        position: absolute;
        top: 50%;
        right: ${toRem(13.75)};
        width: ${toRem(16)};
        height: ${toRem(16)};
        font-size: ${toRem(20)};
        line-height: ${toRem(12)};
        background: ${theme.color.primary[600]};
        color: #ffffff;
        border-radius: 50%;
        transform: translate(0, -50%);
        text-align: center;
      }
    }

    &-no-data {
      display: none;
    }
  }

  .react-dropdown-select[disabled] .react-dropdown-select-content {
    background-color: ${theme.color.neutral[100]};

    input {
      background-color: ${theme.color.neutral[100]};
    }
  }
`;

const Text = styled.span`
  display: inline-block;
  min-height: ${toRem(16)};
  line-height: ${toRem(18)};
  max-width: calc(100% - ${toRem(26)});
  word-wrap: break-word;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

export const creatableItemRenderer = <T extends any>({ item, itemIndex, methods, state }: SelectItemRenderer<ICreatableSelectOption<T>>) => {
  if (item.disabled) {
    return (
      <CreatableSelectOption disabled aria-label={`Option: ${item.label} is not available to choose.`} aria-selected={false} key={itemIndex}>
        <div>
          <Text>{item.label}</Text>
        </div>
      </CreatableSelectOption>
    );
  }

  const isSelected = item.label === state.values[0]?.label;

  return (
    <CreatableSelectOption onClick={() => methods.addItem(item)} className={isSelected ? 'active' : ''} aria-label={`Option: ${item.label}${isSelected ? ' is selected' : ''}`} aria-selected={isSelected} key={itemIndex}>
      <div>
        <Text>{item.label}</Text>
      </div>
    </CreatableSelectOption>
  );
};

export const CreatableSelect = <T extends any>({
  className = '',
  disableCreationOnEnter,
  inputProps,
  inputRenderer,
  itemRenderer,
  multi,
  onChange,
  onCreate,
  onDropdownCloseRequest,
  onEnterPress,
  onInputBlur,
  onInputChange,
  onInputClick,
  onInputFocus,
  onInputKeypress,
  options,
  values = [],
  valueField = 'value',
  ...rest
}: IProps<T>) => {
  const [activeOption, setActiveOption] = useState<number>();
  const id = useRef(`cs-${uuidv4()}`).current;

  useEffect(() => {
    if (typeof activeOption !== 'number') return;

    const dropdown = document.querySelector(`#${id} .react-dropdown-select-dropdown`);
    const el = document.querySelector(`#${id} span.active`);
    if (dropdown && el) {
      const dropdownBounding = dropdown.getBoundingClientRect();
      const bounding = el.getBoundingClientRect();
      if (bounding.top >= dropdownBounding.top && bounding.bottom <= dropdownBounding.bottom) return;

      el.scrollIntoView();
    }
  }, [activeOption]);

  // https://github.com/sanusart/react-dropdown-select/blob/master/src/components/Input.js
  const handlePlaceHolder = useCallback((props: SelectProps<ICreatableSelectOption<T>>, state: SelectState<ICreatableSelectOption<T>>) => {
    const { addPlaceholder, searchable, placeholder } = props;
    const noValues = state.values && state.values.length === 0;
    const hasValues = state.values && state.values.length > 0;

    if (hasValues && addPlaceholder && searchable) {
      return addPlaceholder;
    }

    if (noValues) {
      return placeholder;
    }

    if (hasValues && !searchable) {
      return '';
    }

    return '';
  }, []);

  // modified code from https://github.com/sanusart/react-dropdown-select/blob/master/src/components/Input.js
  const creatableInputRenderer = useCallback(
    ({ inputRef, methods, props, state }: SelectRenderer<ICreatableSelectOption<T>> & { inputRef: React.RefObject<HTMLInputElement> }) => {
      const onCreatableInputBlur: FocusEventHandler<HTMLInputElement> = (e) => {
        e.stopPropagation();
        inputRef?.current?.[!state.dropdown ? 'blur' : 'focus']?.();
        onInputBlur?.(e);
      };

      const onCreatableInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        if (!e.target.value || !!e.target.value.trim()) {
          methods.setSearch(e);
          methods.dropDown('open');
          onInputChange?.(e);
        }
      };

      const onCreatableInputClick: MouseEventHandler = (e) => {
        onInputClick?.(e);
      };

      const onCreatableInputFocus: FocusEventHandler<HTMLInputElement> = (e) => {
        e.stopPropagation();
        onInputFocus?.(e);
      };

      const onCreatableInputKeypress: KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (e.key === 'Enter') {
          // required to prevent form submission via Enter keypress
          e.preventDefault();

          if (!onEnterPress && !disableCreationOnEnter) {
            methods.createNew(state.search.trim());
            return;
          }

          const result = onEnterPress({ inputRef, methods, props, state });

          switch (result.action) {
            case CreatableEnterAction.Create:
              methods.createNew(state.search.trim());
              methods.dropDown('close');
              break;
            case CreatableEnterAction.Select:
              if (!result.optionToSelect) return;
              methods.clearAll();
              methods.addItem(result.optionToSelect as ICreatableSelectOption<T>);
              methods.dropDown('close');
              break;
            case CreatableEnterAction.None:
            default:
              break;
          }
        }

        onInputKeypress?.(e);
      };

      const onCreatableInputKeydown: KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (((e.key === 'Backspace' && props.backspaceDelete) || e.key === 'Delete') && !(e.target as HTMLInputElement).value) {
          methods.clearAll();
        }
      };

      return (
        <input
          {...props}
          {...(inputProps || {})}
          ref={inputRef}
          className={`react-dropdown-select-input ${className ? `${className}-input` : ''}`}
          disabled={props.disabled}
          placeholder={handlePlaceHolder(props, state)}
          readOnly={!props.searchable}
          size={methods.getInputSize()}
          tabIndex={-1}
          value={state.search}
          onBlur={onCreatableInputBlur}
          onChange={onCreatableInputChange}
          onClick={onCreatableInputClick}
          onFocus={onCreatableInputFocus}
          onKeyDown={onCreatableInputKeydown}
          onKeyPress={onCreatableInputKeypress}
        />
      );
    },
    [onInputBlur, onInputChange, onInputClick, onInputFocus, onInputKeypress, className, inputProps, disableCreationOnEnter]
  );

  // eslint-disable-next-line consistent-return
  const handleKeyDownFn = useCallback(({ event, state, props, methods, setState }: SelectKeyDown<ICreatableSelectOption<T>>) => {
    const { cursor } = state;
    const searchResults = methods.searchResults();
    const escape = event.key === 'Escape';
    const enter = event.key === 'Enter';
    const arrowUp = event.key === 'ArrowUp';
    const arrowDown = event.key === 'ArrowDown';
    const backspace = event.key === 'Backspace';
    const tab = event.key === 'Tab' && !event.shiftKey;
    const shiftTab = event.shiftKey && event.key === 'Tab';

    if (arrowDown && !state.dropdown) {
      event.preventDefault();
      methods.dropDown('open');
      return setState({
        cursor: 0,
      });
    }

    if ((arrowDown || (tab && state.dropdown)) && cursor === null) {
      return setState({
        cursor: 0,
      });
    }

    if (escape) {
      methods.dropDown('close');
    }

    if (enter) {
      const currentItem = searchResults[cursor];

      const getByPath = (object: ICreatableSelectOption<T>, path: string) => {
        if (!path) return;

        // eslint-disable-next-line consistent-return
        return path.split('.').reduce((acc, value) => acc[value as keyof object], object) as unknown as string;
      };

      const valueExistInSelected = (value: string, values: ICreatableSelectOption<T>[], props: SelectProps<ICreatableSelectOption<T>>) =>
        !!values.find((val) => getByPath(val, props.valueField) === value || getByPath(val, props.labelField) === value);

      if (currentItem && !currentItem.disabled) {
        if (props.create && valueExistInSelected(state.search, state.values, props)) {
          return null;
        }

        methods.addItem(currentItem);
      }
    }

    if ((arrowDown || (tab && state.dropdown)) && searchResults.length === cursor) {
      setState({
        cursor: 0,
      });
      return setActiveOption(0);
    }

    if (arrowDown || (tab && state.dropdown)) {
      setState((prevState) => ({ cursor: prevState.cursor }));
      return setActiveOption(cursor + 1);
    }

    if ((arrowUp || (shiftTab && state.dropdown)) && cursor > 0) {
      setState((prevState) => ({
        cursor: prevState.cursor,
      }));
      setActiveOption(cursor - 1);
    }

    if ((arrowUp || (shiftTab && state.dropdown)) && cursor === 0) {
      setState({
        cursor: searchResults.length,
      });

      setActiveOption(searchResults.length);
    }

    if (backspace && props.backspaceDelete && methods.getInputSize() === 0) {
      setState({
        values: state.values.slice(0, -1),
      });
    }
  }, []);

  return (
    <CreatableSelectLayout<string | ComponentType<SelectProps<ICreatableSelectOption<T>> & { id: string }>>
      {...rest}
      create
      className={className}
      handleKeyDownFn={handleKeyDownFn}
      id={id}
      inputRenderer={inputRenderer || creatableInputRenderer}
      itemRenderer={itemRenderer || creatableItemRenderer}
      labelField="label"
      onChange={onChange}
      onCreateNew={onCreate}
      onDropdownCloseRequest={onDropdownCloseRequest}
      options={options}
      searchable
      valueField={valueField}
      values={values}
    />
  );
};
